/**
 * @name HomePageExtratoMovimentacoesDialog
 * @description Modal de extrato de movimentacoes no padrao visual do admin
 */

import { useUserContext } from '@/contexts/UserContext';
import { vwExtratoMovimentacoesAlunoService } from '@/services/supabase/views/VwExtratoMovimentacoesAlunoService';
import { exportData } from '@/utils/secureExportUtils';
import { supabase } from '../../../../supabaseConfig';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
     Alert,
     Box,
     Button,
     Chip,
     CircularProgress,
     Dialog,
     DialogActions,
     DialogContent,
     DialogTitle,
     Divider,
     FormControl,
     Grid,
     IconButton,
     InputLabel,
     MenuItem,
     Paper,
     Select,
     Tooltip,
     Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridToolbarContainer } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { ptBR as dateFnsPtBR } from 'date-fns/locale';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface HomePageExtratoMovimentacoesDialogProps {
     open: boolean;
     onClose: () => void;
}

interface MovimentacaoRow {
     id: number | null;
     fk_id_aluno?: number | null;
     pessoa_nome?: string | null;
     tipo_movimentacao: string | null;
     tipo_origem: string | null;
     valor: number | null;
     saldo_anterior: number | null;
     saldo_posterior: number | null;
     descricao: string | null;
     descricao_detalhada?: string | null;
     criado_por_nome?: string | null;
     data_movimentacao: string | null;
}

interface DependenteOption {
     id: number;
     nome: string;
}

const CustomToolbar: React.FC<{ onExportExcel: () => void; onRefresh: () => void }> = ({ onExportExcel, onRefresh }) => (
     <GridToolbarContainer>
          <Box
               display='flex'
               gap={1}
          >
               <Tooltip title='Atualizar dados'>
                    <IconButton
                         size='small'
                         onClick={onRefresh}
                    >
                         <RefreshIcon />
                    </IconButton>
               </Tooltip>
               <Tooltip title='Exportar Excel'>
                    <IconButton
                         size='small'
                         onClick={onExportExcel}
                    >
                         <DownloadIcon />
                    </IconButton>
               </Tooltip>
          </Box>
     </GridToolbarContainer>
);

export const HomePageExtratoMovimentacoesDialog: React.FC<HomePageExtratoMovimentacoesDialogProps> = ({ open, onClose }) => {
     const { userData } = useUserContext();

     const [loading, setLoading] = useState(false);
     const [error, setError] = useState<string | null>(null);
     const [movimentacoes, setMovimentacoes] = useState<MovimentacaoRow[]>([]);
     const [dependentes, setDependentes] = useState<DependenteOption[]>([]);
     const [dependenteSelecionado, setDependenteSelecionado] = useState<'todos' | number>('todos');
     const [dataInicio, setDataInicio] = useState<string>(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
     const [dataFim, setDataFim] = useState<string>(dayjs().format('YYYY-MM-DD'));
     const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

     const formatCurrency = (value: string | number | null): string => {
          if (!value) return 'R$ 0,00';
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          return new Intl.NumberFormat('pt-BR', {
               style: 'currency',
               currency: 'BRL',
          }).format(numValue);
     };

     const formatDate = (dateString: string | null): string => {
          if (!dateString) return '-';
          try {
               return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: dateFnsPtBR });
          } catch {
               return dateString;
          }
     };

     const getTipoMovimentacaoColor = (tipo: string | null): 'success' | 'error' | 'default' => {
          switch (tipo) {
               case 'credito':
                    return 'success';
               case 'debito':
                    return 'error';
               default:
                    return 'default';
          }
     };

     const getTipoMovimentacaoLabel = (tipo: string | null): string => {
          switch (tipo) {
               case 'credito':
                    return 'Credito';
               case 'debito':
                    return 'Debito';
               default:
                    return tipo || 'Nao informado';
          }
     };

     const getTipoOrigemLabel = (tipoOrigemFilter: string | null): string => {
          switch (tipoOrigemFilter) {
               case 'compra':
                    return 'Compra';
               case 'transferencia':
                    return 'Transferencia';
               case 'deposito':
                    return 'Deposito';
               case 'estorno':
                    return 'Estorno';
               case 'ajuste':
                    return 'Ajuste';
               case 'credito_manual':
                    return 'Credito Manual';
               case 'debito_manual':
                    return 'Debito Manual';
               default:
                    return tipoOrigemFilter || 'Nao informado';
          }
     };

     const fetchData = useCallback(async () => {
          if (!userData?.id || !userData?.fk_id_organizacao) return;

          const organizationId = userData.fk_id_organizacao;
          const responsavelId = userData.id;

          setLoading(true);
          setError(null);

          try {
               // Carregar dependentes
               const { data: dependentesData, error: dependentesError } = await supabase
                    .from('pessoas_dependentes')
                    .select('fk_id_aluno, aluno:pessoas!fk_id_aluno(id, nome)')
                    .eq('fk_id_organizacao', organizationId)
                    .eq('fk_id_responsavel', responsavelId);

               if (dependentesError) {
                    setError(dependentesError.message || 'Erro ao carregar dependentes.');
                    setMovimentacoes([]);
                    setDependentes([]);
                    return;
               }

               const dependentesValidos: DependenteOption[] = (dependentesData || [])
                    .map((item: any) => {
                         const aluno = Array.isArray(item.aluno) ? item.aluno[0] : item.aluno;
                         if (!aluno?.id) return null;
                         return {
                              id: Number(aluno.id),
                              nome: aluno.nome || `Dependente ${aluno.id}`,
                         };
                    })
                    .filter((item: DependenteOption | null): item is DependenteOption => Boolean(item));

               setDependentes(dependentesValidos);

               if (dependentesValidos.length === 0) {
                    setMovimentacoes([]);
                    return;
               }

               // Determinar quais dependentes buscar
               const dependentesBuscar = dependenteSelecionado === 'todos' 
                    ? dependentesValidos 
                    : dependentesValidos.filter(dep => dep.id === dependenteSelecionado);

               if (dependentesBuscar.length === 0) {
                    setMovimentacoes([]);
                    return;
               }

               // Buscar movimentações para cada dependente no período
               const dataInicio_str = dayjs(dataInicio).format('YYYY-MM-DD');
               const dataFim_str = dayjs(dataFim).format('YYYY-MM-DD');

               const respostasMovimentacoes = await Promise.all(
                    dependentesBuscar.map((dependente) => {
                         console.log(`🔄 Carregando movimentações para aluno ID: ${dependente.id} (${dependente.nome})`);
                         return vwExtratoMovimentacoesAlunoService.getMovimentacoesPorPeriodo(
                              organizationId,
                              dependente.id,
                              dataInicio_str,
                              dataFim_str
                         );
                    }),
               );

               console.log('📊 Respostas recebidas:', respostasMovimentacoes);

               const primeiraComErro = respostasMovimentacoes.find((response) => response.error);
               if (primeiraComErro?.error) {
                    console.error('❌ Erro na query:', primeiraComErro.error);
                    setError(primeiraComErro.error.message || 'Erro ao carregar extrato de movimentacoes.');
                    setMovimentacoes([]);
                    return;
               }

               // Consolidar dados
               const dataFlat = respostasMovimentacoes.flatMap((response) => {
                    const data = response.data || [];
                    console.log('📦 Dados de uma resposta:', data);
                    return Array.isArray(data) ? data : [data];
               });

               console.log('🎯 Total de movimentações consolidadas:', dataFlat.length);

               // Mapear para o formato esperado
               const filtradas = dataFlat
                    .map((mov: any) => ({
                         id: mov.id ?? null,
                         fk_id_aluno: mov.fk_id_aluno ?? null,
                         pessoa_nome: mov.nome_aluno ?? null,
                         tipo_movimentacao: mov.tipo_movimentacao ?? null,
                         tipo_origem: mov.tipo_origem ?? null,
                         valor: mov.valor ?? null,
                         saldo_anterior: mov.saldo_anterior ?? null,
                         saldo_posterior: mov.saldo_posterior ?? null,
                         descricao: mov.descricao ?? null,
                         descricao_detalhada: mov.descricao ?? null,
                         criado_por_nome: mov.criado_por ?? null,
                         data_movimentacao: mov.data_movimentacao ?? null,
                    }))
                    .sort((a: MovimentacaoRow, b: MovimentacaoRow) => {
                         if (!a.data_movimentacao || !b.data_movimentacao) return 0;
                         return dayjs(b.data_movimentacao).valueOf() - dayjs(a.data_movimentacao).valueOf();
                    });

               setMovimentacoes(filtradas);
          } catch (err) {
               const mensagem = err instanceof Error ? err.message : 'Erro inesperado ao carregar extrato.';
               setError(mensagem);
               setMovimentacoes([]);
          } finally {
               setLoading(false);
          }
     }, [userData?.id, userData?.fk_id_organizacao, dataInicio, dataFim, dependenteSelecionado]);

     useEffect(() => {
          if (open) {
               fetchData();
          }
     }, [open, fetchData]);

     useEffect(() => {
          if (dependenteSelecionado !== 'todos' && !dependentes.some((dep) => dep.id === dependenteSelecionado)) {
               setDependenteSelecionado('todos');
          }
     }, [dependentes, dependenteSelecionado]);

     const movimentacoesFiltradas = useMemo(
          () =>
               dependenteSelecionado === 'todos'
                    ? movimentacoes
                    : movimentacoes.filter((mov) => Number(mov.fk_id_aluno) === Number(dependenteSelecionado)),
          [movimentacoes, dependenteSelecionado],
     );

     const resumo = useMemo(() => {
          const saldoAtual = movimentacoesFiltradas.length > 0 ? movimentacoesFiltradas[0].saldo_posterior ?? 0 : 0;
          const totalCreditos = movimentacoesFiltradas.filter((mov) => mov.tipo_movimentacao === 'credito').reduce((acc, mov) => acc + (mov.valor ?? 0), 0);
          const totalDebitos = movimentacoesFiltradas.filter((mov) => mov.tipo_movimentacao === 'debito').reduce((acc, mov) => acc + (mov.valor ?? 0), 0);

          return {
               saldoAtual,
               totalCreditos,
               totalDebitos,
               saldoLiquido: totalCreditos - totalDebitos,
               totalMovimentacoes: movimentacoesFiltradas.length,
          };
     }, [movimentacoesFiltradas]);

     const handleExportExcel = useCallback(() => {
          if (movimentacoesFiltradas.length === 0) return;

          const dadosExport = movimentacoesFiltradas.map((mov) => ({
               Dependente: mov.pessoa_nome || 'Nao informado',
               Data: formatDate(mov.data_movimentacao),
               Tipo: getTipoMovimentacaoLabel(mov.tipo_movimentacao),
               Origem: getTipoOrigemLabel(mov.tipo_origem),
               Descricao: mov.descricao_detalhada || mov.descricao || 'Sem descricao',
               Valor: mov.valor ?? 0,
               SaldoAnterior: mov.saldo_anterior ?? 0,
               SaldoPosterior: mov.saldo_posterior ?? 0,
          }));

          exportData(dadosExport, 'excel', {
               filename: `extrato_movimentacoes_${dayjs().format('YYYY-MM-DD')}`,
               sheetName: 'Extrato',
          });
     }, [movimentacoesFiltradas]);

     const rows = useMemo(() => movimentacoesFiltradas.map((row, index) => ({ ...row, id: row.id ?? index + 1 })), [movimentacoesFiltradas]);

     const columns = useMemo<GridColDef[]>(
          () => [
               {
                    field: 'data_movimentacao',
                    headerName: 'Data',
                    width: 160,
                    renderCell: (params) => (
                         <Typography
                              variant='body2'
                              sx={{ px: 1 }}
                         >
                              {formatDate(params.value)}
                         </Typography>
                    ),
               },
               {
                    field: 'pessoa_nome',
                    headerName: 'Dependente',
                    minWidth: 180,
                    flex: 1,
                    renderCell: (params) => (
                         <Typography
                              variant='body2'
                              sx={{ px: 1 }}
                         >
                              {params.value || 'Nao informado'}
                         </Typography>
                    ),
               },
               {
                    field: 'tipo_movimentacao',
                    headerName: 'Tipo',
                    width: 110,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => (
                         <Box sx={{ px: 1 }}>
                              <Chip
                                   label={getTipoMovimentacaoLabel(params.value)}
                                   color={getTipoMovimentacaoColor(params.value)}
                                   size='small'
                              />
                         </Box>
                    ),
               },
               {
                    field: 'tipo_origem',
                    headerName: 'Origem',
                    width: 130,
                    renderCell: (params) => (
                         <Typography
                              variant='body2'
                              fontWeight='medium'
                              sx={{ px: 1 }}
                         >
                              {getTipoOrigemLabel(params.value)}
                         </Typography>
                    ),
               },
               {
                    field: 'descricao_detalhada',
                    headerName: 'Descricao',
                    flex: 1,
                    minWidth: 220,
                    renderCell: (params) => (
                         <Box sx={{ px: 1, py: 0.5 }}>
                              <Typography variant='body2'>{params.value || params.row.descricao || 'Sem descricao'}</Typography>
                              {params.row.criado_por_nome && (
                                   <Typography
                                        variant='caption'
                                        color='text.secondary'
                                   >
                                        por: {params.row.criado_por_nome}
                                   </Typography>
                              )}
                         </Box>
                    ),
               },
               {
                    field: 'valor',
                    headerName: 'Valor',
                    width: 130,
                    align: 'right',
                    headerAlign: 'right',
                    renderCell: (params) => (
                         <Typography
                              variant='body2'
                              fontWeight='bold'
                              color={params.row.tipo_movimentacao === 'credito' ? 'success.main' : 'error.main'}
                              sx={{ px: 1 }}
                         >
                              {params.row.tipo_movimentacao === 'credito' ? '+' : '-'}
                              {formatCurrency(params.value)}
                         </Typography>
                    ),
               },
               {
                    field: 'saldo_anterior',
                    headerName: 'Saldo Anterior',
                    width: 140,
                    align: 'right',
                    headerAlign: 'right',
                    renderCell: (params) => (
                         <Typography
                              variant='body2'
                              sx={{ px: 1 }}
                         >
                              {formatCurrency(params.value)}
                         </Typography>
                    ),
               },
               {
                    field: 'saldo_posterior',
                    headerName: 'Saldo Posterior',
                    width: 140,
                    align: 'right',
                    headerAlign: 'right',
                    renderCell: (params) => (
                         <Typography
                              variant='body2'
                              fontWeight='medium'
                              sx={{ px: 1 }}
                         >
                              {formatCurrency(params.value)}
                         </Typography>
                    ),
               },
          ],
          [],
     );

     const matricula = (userData as unknown as { matricula?: string | number } | null)?.matricula;

     return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
               <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth='xl'
                    fullWidth
                    PaperProps={{
                         sx: { minHeight: '80vh', maxHeight: '90vh' },
                    }}
               >
                    <DialogTitle>
                         <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='space-between'
                         >
                              <Typography
                                   variant='h6'
                                   component='div'
                              >
                                   Extrato de Movimentacoes - {userData?.nome || 'Responsavel'}
                              </Typography>
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                              >
                                   {matricula ? `Matricula: ${matricula}` : ''}
                              </Typography>
                         </Box>
                    </DialogTitle>

                    <DialogContent>
                    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 1 }}>
                         <Grid
                              container
                              spacing={2}
                              alignItems='center'
                         >
                              <Grid
                                   item
                                   xs={12}
                                   sm={4}
                                   md={3}
                              >
                                   <DatePicker
                                        label='Data Inicio'
                                        value={dayjs(dataInicio)}
                                        onChange={(date) => setDataInicio(date ? date.format('YYYY-MM-DD') : '')}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        format='DD/MM/YYYY'
                                   />
                              </Grid>
                              <Grid
                                   item
                                   xs={12}
                                   sm={4}
                                   md={3}
                              >
                                   <DatePicker
                                        label='Data Fim'
                                        value={dayjs(dataFim)}
                                        onChange={(date) => setDataFim(date ? date.format('YYYY-MM-DD') : '')}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        format='DD/MM/YYYY'
                                   />
                              </Grid>
                              <Grid
                                   item
                                   xs={12}
                                   sm={4}
                                   md={3}
                              >
                                   <FormControl
                                        fullWidth
                                        size='small'
                                   >
                                        <InputLabel id='dependente-select-label'>Dependente</InputLabel>
                                        <Select
                                             labelId='dependente-select-label'
                                             label='Dependente'
                                             value={dependenteSelecionado}
                                             onChange={(event) => {
                                                  const value = event.target.value;
                                                  setDependenteSelecionado(value === 'todos' ? 'todos' : Number(value));
                                             }}
                                        >
                                             <MenuItem value='todos'>Todos os dependentes</MenuItem>
                                             {dependentes.map((dependente) => (
                                                  <MenuItem
                                                       key={dependente.id}
                                                       value={dependente.id}
                                                  >
                                                       {dependente.nome}
                                                  </MenuItem>
                                             ))}
                                        </Select>
                                   </FormControl>
                              </Grid>
                              <Grid item>
                                   <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={fetchData}
                                        disabled={loading}
                                        sx={{ minWidth: 100 }}
                                   >
                                        Buscar
                                   </Button>
                              </Grid>
                              <Grid item>
                                   <Button
                                        variant='outlined'
                                        onClick={() => {
                                             setDataInicio(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
                                             setDataFim(dayjs().format('YYYY-MM-DD'));
                                             setDependenteSelecionado('todos');
                                        }}
                                        disabled={loading}
                                   >
                                        Limpar
                                   </Button>
                              </Grid>
                         </Grid>
                    </Paper>

                    {loading && (
                         <Box
                              display='flex'
                              justifyContent='center'
                              alignItems='center'
                              minHeight='300px'
                         >
                              <CircularProgress />
                         </Box>
                    )}

                    {error && (
                         <Alert
                              severity='error'
                              sx={{ mb: 2 }}
                         >
                              {error}
                         </Alert>
                    )}

                    {!loading && !error && movimentacoesFiltradas.length === 0 && <Alert severity='info'>Nenhuma movimentacao encontrada para o periodo selecionado.</Alert>}

                    {!loading && !error && movimentacoesFiltradas.length > 0 && (
                         <>
                              <Box
                                   mb={2}
                                   p={2}
                                   bgcolor='background.paper'
                                   borderRadius={2}
                                   sx={{ border: 1, borderColor: 'divider', boxShadow: 1 }}
                              >
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Resumo Financeiro do Periodo
                                   </Typography>
                                   <Box
                                        display='flex'
                                        gap={4}
                                        flexWrap='wrap'
                                   >
                                        <Typography variant='body1'>
                                             <strong>Saldo Atual:</strong> {formatCurrency(resumo.saldoAtual)}
                                        </Typography>
                                        <Typography variant='body1'>
                                             <strong>Movimentacoes:</strong> {resumo.totalMovimentacoes}
                                        </Typography>
                                   </Box>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Paper
                                   sx={{
                                        height: 400,
                                        width: '100%',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: 2,
                                   }}
                              >
                                   <DataGrid
                                        rows={rows}
                                        columns={columns}
                                        paginationModel={paginationModel}
                                        onPaginationModelChange={setPaginationModel}
                                        pageSizeOptions={[25, 50, 100]}
                                        disableRowSelectionOnClick
                                        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                                        sx={{
                                             '& .MuiDataGrid-root': {
                                                  borderRadius: 2,
                                             },
                                             '& .MuiDataGrid-main': {
                                                  borderRadius: '8px 8px 0 0',
                                             },
                                             '& .MuiDataGrid-columnHeaders': {
                                                  borderRadius: '8px 8px 0 0',
                                                  backgroundColor: 'primary.main',
                                                  color: 'primary.contrastText',
                                             },
                                             '& .MuiDataGrid-footerContainer': {
                                                  borderTop: 1,
                                                  borderColor: 'divider',
                                                  borderRadius: '0 0 8px 8px',
                                             },
                                             '& .MuiDataGrid-cell': {
                                                  borderBottom: '1px solid',
                                                  borderColor: 'divider',
                                                  '&:focus': {
                                                       outline: 'none',
                                                  },
                                             },
                                             '& .MuiDataGrid-row': {
                                                  '&:hover': {
                                                       backgroundColor: 'action.hover',
                                                  },
                                             },
                                        }}
                                        slots={{
                                             toolbar: () => (
                                                  <CustomToolbar
                                                       onExportExcel={handleExportExcel}
                                                       onRefresh={fetchData}
                                                  />
                                             ),
                                        }}
                                   />
                              </Paper>
                         </>
                    )}
                    </DialogContent>

                    <DialogActions>
                         <Button onClick={onClose}>Fechar</Button>
                    </DialogActions>
               </Dialog>
          </LocalizationProvider>
     );
};
